import { useEffect } from 'react';

import { useBlocker } from 'react-router-dom';

/**
 * Stop a navigation, and a reload, while there is unsaved work.
 *
 * Two separate mechanisms, because they cover different things and neither covers
 * both:
 *
 * - `useBlocker` catches navigation *inside* the application: clicking the sidebar,
 *   the browser's back button. It needs a data router, which is why the application
 *   uses one.
 * - `beforeunload` catches leaving the page entirely: a reload, a closed tab, a typed
 *   address. The browser shows its own wording here; nothing we pass is displayed.
 */
export function useUnsavedChanges(dirty: boolean) {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (!dirty) return;

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      // preventDefault is the whole signal now. The browser shows its own wording;
      // nothing we could pass would be displayed.
      event.preventDefault();
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [dirty]);

  return {
    /** True while a navigation is held, waiting for an answer. */
    blocked: blocker.state === 'blocked',
    /** Let the navigation through, discarding the changes. */
    discard: () => blocker.proceed?.(),
    /** Stay where we are. */
    stay: () => blocker.reset?.(),
  };
}
